import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/Logo";
import { ConnectFooter } from "@/components/connect/ConnectFooter";
import { ConnectPortalLayout } from "@/components/layout/ConnectPortalLayout";
import { MARKETING_CONFIG } from "@/config/marketing";
import { DialerLanguageToggle } from "@/components/connect/DialerLanguageToggle";
import { Phone, Globe, Clock, CreditCard } from "lucide-react";
import { getFlagSvgUrl, iso2ToFlagEmoji } from "@/lib/country-flags";
import { useCountryRates } from "@/hooks/useCountryRates";
import { getDisplayCurrencyForCountry } from "@/config/pricing/exchange-rates";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { SEOHead } from "@/components/seo/SEOHead";
import { getCanonicalUrl, getBreadcrumbSchema } from "@/config/seo";
import { useIsNative } from "@/hooks/useIsNative";

export interface DiasporaOriginConfig {
  name: string;
  slug: string;
  iso2: string;
  flag: string;
  emergencyNumber: string;
  regulatoryNotice: string;
  currency: string;
  popularDestinations: { name: string; flag: string; slug: string; dialCode: string }[];
  seoTitle: string;
  seoDescription: string;
}

interface DiasporaConnectPageProps {
  origin: DiasporaOriginConfig;
}

function DiasporaContent({ origin, isAuthenticated }: { origin: DiasporaOriginConfig; isAuthenticated: boolean }) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { getRateForCountry, isLoading } = useCountryRates();
  const isNative = useIsNative();

  const topDest = origin.popularDestinations[0];
  const topRate = topDest ? getRateForCountry(topDest.dialCode) : undefined;

  // Force landing-page price into the origin country's currency (e.g.
  // /call-from-australia → AUD, /call-from-uk → GBP). The caller-country
  // hook is geo-personalised and would otherwise show whatever currency
  // the visitor's IP/locale maps to, which is wrong for a country-targeted
  // marketing page.
  const originCurrency = getDisplayCurrencyForCountry(origin.iso2);
  const formatOriginRate = (usdCents: number): string => {
    const localDollars = (usdCents * originCurrency.rate) / 100;
    return `${originCurrency.symbol}${localDollars.toFixed(2)}`;
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <img
              src={getFlagSvgUrl(iso2ToFlagEmoji(origin.iso2))}
              alt={origin.name}
              className="w-20 h-14 object-cover rounded-sm mx-auto mb-6 shadow-md"
            />
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold mb-6 tracking-tight">
              {t("callHomeFrom", { country: origin.name })}
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-6">
              {MARKETING_CONFIG.connectTagline}
            </p>

            {topRate && topDest && (
              <div className="mb-10">
                <p className="text-muted-foreground mb-2">{t("callsToFrom", { country: topDest.name })}</p>
                <span className="text-5xl font-bold text-primary">
                  {formatOriginRate(topRate.ratePerMinuteCents)}{t("perMin")}
                </span>
              </div>
            )}

            {isAuthenticated ? (
              <Button size="lg" className="text-lg px-8 py-6" onClick={() => navigate(isNative ? "/connect-portal/dial" : "/connect-portal/topup")}>
                {isNative ? t("callNow") : t("topUpNow", "Top up now")}
              </Button>
            ) : (
              <Link to="/connect-portal/auth">
                <Button size="lg" className="text-lg px-8 py-6">
                  {t("getFreeCreditBtn")}
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Popular Destinations */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-semibold mb-8">{t("popularDestinations")}</h2>
            <div className="flex flex-wrap justify-center gap-4">
              {origin.popularDestinations.map((dest) => (
                <Link
                  key={dest.slug}
                  to={`/connect-portal/${dest.slug}`}
                  className="flex items-center gap-2 px-4 py-2 bg-background rounded-full border hover:border-primary transition-colors"
                >
                  <img
                    src={getFlagSvgUrl(dest.flag)}
                    alt={dest.name}
                    className="w-6 h-4 object-cover rounded-sm"
                  />
                  <span>{dest.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div className="space-y-2">
                <Phone className="h-8 w-8 mx-auto text-primary" />
                <p className="font-medium">{t("hdCalls")}</p>
              </div>
              <div className="space-y-2">
                <Globe className="h-8 w-8 mx-auto text-primary" />
                <p className="font-medium">{t("worldwideReach", "Worldwide Reach")}</p>
              </div>
              <div className="space-y-2">
                <Clock className="h-8 w-8 mx-auto text-primary" />
                <p className="font-medium">{t("noExpiry")}</p>
              </div>
              <div className="space-y-2">
                <CreditCard className="h-8 w-8 mx-auto text-primary" />
                <p className="font-medium">{t("noContracts")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t("startCallingToday")}
            </h2>
            <p className="text-muted-foreground mb-8">
              {t("signUpGetCredit")}
            </p>
            {isAuthenticated ? (
              <Button size="lg" onClick={() => navigate(isNative ? "/connect-portal/dial" : "/connect-portal/topup")}>
                {isNative ? t("callNow") : t("topUpNow", "Top up now")}
              </Button>
            ) : (
              <Link to="/connect-portal/auth">
                <Button size="lg">{t("getStarted")}</Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Regulatory Disclaimer */}
      <div className="container mx-auto px-4 py-6">
        <div className="text-xs text-center text-muted-foreground max-w-lg mx-auto">
          <span className="text-destructive font-semibold">{t("important")}</span> {t("emergencyWarning")}
        </div>
      </div>
    </div>
  );
}

export function DiasporaConnectPage({ origin }: DiasporaConnectPageProps) {
  const { t } = useTranslation("common");
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    const check = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session?.user);
    };
    check();
  }, []);

  if (isAuthenticated === null) {
    return <div className="min-h-[100dvh] bg-background" />;
  }

  if (isAuthenticated) {
    return (
      <ConnectPortalLayout requireAuth={false}>
        <DiasporaContent origin={origin} isAuthenticated={true} />
      </ConnectPortalLayout>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={origin.seoTitle || `Call from ${origin.name} | elefa Connect`}
        description={origin.seoDescription || `Make affordable international calls from ${origin.name}. HD voice quality. Credit never expires.`}
        canonicalUrl={getCanonicalUrl(`/connect-portal/${origin.slug}`)}
        structuredData={getBreadcrumbSchema([
          { name: 'Home', url: 'https://elefa.tel/' },
          { name: `Call from ${origin.name}`, url: `https://elefa.tel/connect-portal/${origin.slug}` },
        ])}
      />
      <nav className="container py-6 flex items-center justify-between border-b">
        <Link to="/connect-portal" className="flex items-center gap-2">
          <Logo variant="header" size="md" asLink={false} withIcon fullName />
          <span className="text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full font-medium inline-flex items-center translate-y-0.5">
            Connect
          </span>
        </Link>
        <div className="flex items-center gap-3">
          <DialerLanguageToggle />
          <Link to="/connect-portal/auth">
            <Button variant="ghost" size="sm">{t("logIn")}</Button>
          </Link>
          <Link to="/connect-portal/auth">
            <Button size="sm">{t("getStarted")}</Button>
          </Link>
        </div>
      </nav>

      <DiasporaContent origin={origin} isAuthenticated={false} />
      <ConnectFooter />
    </div>
  );
}
